import { useCallback, useEffect, useState } from "react";
import {
  type AiConnection,
  connectedProviders,
  type Insight,
  type InsightRequest,
  insightsView,
  type Provider,
  type SectionContent,
  type SectionKey,
} from "./checkin-insights";

const POLL_MS = 5000;

const requestJson = async (url: string, init: RequestInit = {}) => {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: init.body
      ? { "Content-Type": "application/json", ...init.headers }
      : init.headers,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const { error } = data || {};
    throw new Error(error?.message || error || response.statusText);
  }
  return data;
};

export const useCheckinInsights = (checkinId: string | null) => {
  const [insight, setInsight] = useState<Insight | null>(null);
  const [request, setRequest] = useState<InsightRequest | null>(null);
  const [connections, setConnections] = useState<Provider[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const base = checkinId
    ? `/actions/checkin-insights/${encodeURIComponent(checkinId)}`
    : null;

  const refresh = useCallback(async () => {
    if (!base) return;
    try {
      const data = await requestJson(base);
      setInsight(data.insight ?? null);
      setRequest(data.request ?? null);
      setLoaded(true);
    } catch {
      // A failed poll keeps the last state; the next one retries.
    }
    setNow(Date.now());
  }, [base]);

  useEffect(() => {
    setInsight(null);
    setRequest(null);
    setLoaded(false);
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!checkinId) return;
    requestJson("/actions/checkin-insights/connections")
      .then((data) =>
        setConnections(
          connectedProviders((data.connections ?? []) as AiConnection[])
        )
      )
      .catch(() => setConnections([]));
  }, [checkinId]);

  const isPending = request?.status === "pending";
  useEffect(() => {
    if (!isPending) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isPending, refresh]);

  const start = useCallback(
    async (provider: Provider): Promise<InsightRequest> => {
      const data = await requestJson(`${base}/requests`, {
        method: "POST",
        body: JSON.stringify({ provider }),
      });
      setRequest(data.request);
      setNow(Date.now());
      return data.request;
    },
    [base]
  );

  const cancel = useCallback(async () => {
    if (!request) return;
    await requestJson(`${base}/requests/${encodeURIComponent(request.id)}`, {
      method: "DELETE",
    });
    await refresh();
  }, [base, request, refresh]);

  const saveSection = useCallback(
    async (section: SectionKey, content: SectionContent) => {
      const data = await requestJson(base, {
        method: "PATCH",
        body: JSON.stringify({ section, content: JSON.stringify(content) }),
      });
      setInsight(data.insight);
    },
    [base]
  );

  return {
    insight,
    request,
    connections,
    loaded,
    view: insightsView(insight, request, now),
    start,
    cancel,
    saveSection,
  };
};

export type CheckinInsightsState = ReturnType<typeof useCheckinInsights>;
